import {
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import api from "../../../services/QualityAssurance";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableLoader from "../../TableLoader";

const normalizeArray = (arr, length, filler = "") => {
    const normalized = [...arr];
    while (normalized.length < length) normalized.push(filler);
    return normalized.slice(0, length);
};

const defaultDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const defaultPeriodsCount = 10;
const defaultSpecials = [
    { period: "Assembly" },
    { period: "Registration" },
    { period: "Short Break" },
    { period: "Mid-day Prayer" },
    { period: "Lunch" },
    { period: "Reading" },
    { period: "Cleaning" },
  ];  

export const ManualTable = ({ setupId }) => {
    const TOTAL_COLUMNS = 18;
    const { admToken } = useSelector((state) => state.auth);
    const [timetable, setTimetable] = useState([]);
    const [specialPeriods, setSpecialPeriods] = useState([]);
    const [loading, setLoading] = useState(false);

    const [timeSlots, setTimeSlots] = useState(
        Array.from({ length: TOTAL_COLUMNS }, () => ({ start: "", end: "" }))
    );

    const [fridayTimeSlots, setFridayTimeSlots] = useState(
        Array.from({ length: TOTAL_COLUMNS }, () => ({ start: "", end: "" }))
    );

    const normalizeSlots = (slots, totalLength) => {
        const normalized = [...slots];
        while (normalized.length < totalLength) {
            normalized.push({ start: "", end: "" });
        }
        return normalized.slice(0, totalLength);
    };

    //period
    const fetchPeriod = async () => {
        if (!setupId) return;
        setLoading(true);
        try {
            const res = await api.getTimeTableTimePeriod(admToken, setupId);

            if (res.status && res.statusCode === 2) {
                let { days, specialPeriods } = res.data;
                if (!days || days.length === 0) {
                    const formattedTimetable = defaultDays.map((day) => ({
                        day,
                        periods: Array.from({ length: defaultPeriodsCount }, () => ""),
                    }));

                    setTimetable(formattedTimetable);
                    setSpecialPeriods(defaultSpecials);
                    setLoading(false);
                    return;
                }
                const maxPeriods = Math.max(...days.map((d) => d.periods.length), defaultPeriodsCount);

                const formattedTimetable = days.map((day) => ({
                    day: day.day,
                    periods: normalizeArray(
                        day.periods.map((p) => p.period),
                        maxPeriods
                    ),
                }));

                setTimetable(formattedTimetable);
                setSpecialPeriods(
                    normalizeArray(
                      (specialPeriods || []).map(p =>
                        typeof p === "string" ? { period: p } : p
                      ),
                      defaultSpecials.length,
                      { period: "" }
                    )
                  );                  
            } else {
                const formattedTimetable = defaultDays.map((day) => ({
                    day,
                    periods: Array.from({ length: defaultPeriodsCount }, () => ""),
                }));

                setTimetable(formattedTimetable);
                setSpecialPeriods(defaultSpecials);

                toast.error(res.data.message || res.message);
            }
        } catch (error) {
            console.error("Error fetching periods:", error);

            const formattedTimetable = defaultDays.map((day) => ({
                day,
                periods: Array.from({ length: defaultPeriodsCount }, () => ""),
            }));

            setTimetable(formattedTimetable);
            setSpecialPeriods(defaultSpecials);

            toast.error("Error loading periods");
        } finally {
            setLoading(false);
        }
    };

    //timeslot
    const fetchSlots = async () => {
        if (!setupId) return;
        setLoading(true);
        try {
            const res = await api.getTimeTableTimeSlot(admToken, setupId);
            if (res.status && res.statusCode === 2) {
                const data = res.data;
                const dailySlots =
                    data.find((item) => item.type === "d")?.timeSlotData || [];
                const fridaySlots =
                    data.find((item) => item.type === "f")?.timeSlotData || [];

                setTimeSlots(
                    normalizeSlots(
                        dailySlots.map((slot) => ({
                            start: slot.startTime,
                            end: slot.endTime,
                        })),
                        TOTAL_COLUMNS
                    )
                );

                setFridayTimeSlots(
                    normalizeSlots(
                        fridaySlots.map((slot) => ({
                            start: slot.startTime,
                            end: slot.endTime,
                        })),
                        TOTAL_COLUMNS
                    )
                );

            } else {
                toast.error(res.data.message || res.message);
            }
        } catch (error) {
            console.error("Error fetching slots:", error);
            toast.error("Error loading time slots");
        } finally {
            setLoading(false);
        }
    };

    //specialPeriod
    const fetchSpecialPeriod = async () => {
        if (!setupId) return;
        setLoading(true);
        try {
            const res = await api.getTimeTableTimeSpecialPeriod(admToken, setupId);
            if (res.status && res.statusCode === 2) {
                const specialPeriodsArr =
                    res.data?.[0]?.specialPeriodSlotData || [];

                setSpecialPeriods(
                normalizeArray(
                    specialPeriodsArr.map(item => ({
                    period: typeof item.period === "string"
                        ? item.period
                        : item.period?.period || "",
                    order: item.order,
                    })),
                    defaultSpecials.length,
                    { period: "", order: 0 }
                )
                );
                ;                      
            } else {
                toast.error(res.data.message || res.message);
            }
        } catch (error) {
            console.error("Error fetching special periods:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!setupId) return;

        fetchPeriod();
        fetchSlots();
        fetchSpecialPeriod();
    }, [setupId]);

    //handle input changes for special periods
    const handleSpecialChange = (index, value) => {
        setSpecialPeriods(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            period: value,
          };
          return updated;
        });
    };
    
    const handleSaveSpecialPeriod = async () => {
        try {
            setLoading(true);
            const payload = {
                setupId,
                specialPeriodSlotData: specialPeriods.map((p, i) => ({
                  period: p.period || "",
                  order: i + 1,
                })),
              };              

            const res = await api.createTimeTableSpecialPeriod(admToken, payload);
            if (res.status && res.statusCode === 2) {
                toast.success(res.data.message || res.message);
            } else {
                toast.error(res.data.message || res.message);
            }
        } catch (error) {
            console.error("Error saving special periods:", error);
        } finally {
            setLoading(false);
        }
    };

    // Handle input changes for subjects
    const handleSavePeriod = async (dayOfWeek) => {
        try {
            setLoading(true);

            const dayObj = timetable.find((d) => d.day === dayOfWeek);
            const dayPeriods = dayObj?.periods || [];

            const payload = {
                setupId,
                dayOfWeek,
                periodData: dayPeriods.map((subject, index) => ({
                    periodOrder: index + 1,
                    subjectName: subject || "",
                })),
            };

            const res = await api.createTimeTableTimePeriod(admToken, payload);

            if (res.status && res.statusCode === 2) {
                toast.success(`${dayOfWeek} periods saved successfully`);
            } else {
                toast.error(res.data.message || res.message);
            }
        } catch (error) {
            console.error("Error saving periods:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (dayIndex, periodIndex, value) => {
        setTimetable((prev) => {
            const updated = prev.map((day, i) => {
                if (i === dayIndex) {
                    const newPeriods = [...day.periods];
                    newPeriods[periodIndex] = value;
                    return { ...day, periods: newPeriods };
                }
                return day;
            });
            return updated;
        });
    };
      
    // Handle input changes for start/end
    const handleTimeChange = (slots, setter, index, field, value) => {
        const updated = [...slots];
        updated[index][field] = value;
        setter(updated);
    };

    // Save function (for both daily and friday)
    const handleSave = async (type) => {
        try {
            setLoading(true);

            const slotData =
                type === "f" ? fridayTimeSlots : timeSlots;

            const payload = {
                setupId,
                type,
                timeSlotData: slotData.map((slot, index) => ({
                    startTime: slot.start,
                    endTime: slot.end,
                    timeOrder: index + 1,
                })),
            };
            const res = await api.createTimeTableTimeSlot(admToken, payload);
            if (res.status && res.statusCode === 2) {
                toast.success(res.data.message || res.message);
            } else {
                toast.error(res.data.message || res.message);
            }
        } catch (error) {
            console.error("Error creating calendar:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box mb={10}>
            {loading ? <TableLoader /> :
                <>
                    <Box textAlign="center">
                        <Typography
                            variant="h5"
                            sx={{ fontFamily: "Gilroy-Heavy", fontSize: { xs: "14px", lg: "20px" } }}
                        >
                            CMS GRAMMAR SCHOOL
                        </Typography>
                        <Typography>School Timetable (Junior School)</Typography>
                    </Box>
                    <Box mt={3} className="sheet_table card_class" sx={{ overflowX: "scroll" }}>
                        <table className="timetable w-full border-collapse border border-gray-300 text-sm">
                            <thead className="bg-purple-100 text-gray-800">
                                <tr>
                                    {timeSlots.map((time, i) => (
                                        <th key={i}>
                                            <p className="text-xs">
                                                <input
                                                    type="text"
                                                    value={time.start}
                                                    onChange={(e) =>
                                                        handleTimeChange(timeSlots, setTimeSlots, i, "start", e.target.value)
                                                    }
                                                    className="bg-transparent w-14 text-center focus:outline-none p-1"
                                                />
                                            </p>
                                            <p className="text-xs">
                                                <input
                                                    type="text"
                                                    value={time.end || ""}
                                                    onChange={(e) =>
                                                        handleTimeChange(timeSlots, setTimeSlots, i, "end", e.target.value)
                                                    }
                                                    className="bg-transparent w-14 text-center focus:outline-none p-1"
                                                />
                                            </p>
                                        </th>
                                    ))}
                                    <th>
                                        <button
                                            type="submit"
                                            className="bg-[#691e78] py-1 px-3 rounded text-white text-sm hover:bg-[#691e78]/90 transition"
                                            disabled={loading}
                                            onClick={() => handleSave("d")}
                                        >
                                            Save
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {timetable.map((dayItem, dayIndex) => (
                                    <tr key={dayItem.day}>
                                        <td className="border px-2 py-1 font-semibold">{dayItem.day}</td>

                                        {/* rowSpan special periods */}
                                        {dayIndex === 0 && (
                                            <>
                                                <td rowSpan={5} className="border px-2 py-6 text-center rotate-90">
                                                    <input
                                                        type="text"
                                                        value={specialPeriods[0]?.period  || ""}
                                                        onChange={(e) => handleSpecialChange(0, e.target.value)}
                                                        className="bg-transparent w-40 text-center focus:outline-none"
                                                    />
                                                </td>
                                                <td rowSpan={5} className="border px-2 py-6 text-center rotate-90">
                                                    <input
                                                        type="text"
                                                        value={specialPeriods[1]?.period  || ""}
                                                        onChange={(e) => handleSpecialChange(1, e.target.value)}
                                                        className="bg-transparent w-40 text-center focus:outline-none"
                                                    />
                                                </td>
                                            </>
                                        )}

                                        {/* editable subjects */}
                                        {dayItem.periods.slice(0, 3).map((subj, idx) => (
                                            <td key={idx} className="border px-2 py-1 text-center">
                                                <input
                                                    type="text"
                                                    value={subj}
                                                    onChange={(e) => handlePeriodChange(dayIndex, idx, e.target.value)}
                                                    className="bg-transparent w-full text-center focus:outline-none"
                                                />
                                            </td>
                                        ))}

                                        {dayIndex === 0 && (
                                            <td rowSpan={4} className="border px-2 py-6 text-center rotate-90">
                                                <input
                                                    type="text"
                                                    value={specialPeriods[2]?.period  || ""}
                                                    onChange={(e) => handleSpecialChange(2, e.target.value)}
                                                    className="bg-transparent w-40 text-center focus:outline-none"
                                                />
                                            </td>
                                        )}
                                        {/* Periods 7, 8 */}
                                        {dayItem.periods.slice(3, 5).map((subj, idx) => (
                                            <td key={idx} className="border px-2 py-1 text-center">
                                                <input
                                                    type="text"
                                                    value={subj}
                                                    onChange={(e) => handlePeriodChange(dayIndex, 3 + idx, e.target.value)}
                                                    className="bg-transparent w-full text-center focus:outline-none"
                                                />
                                            </td>
                                        ))}
                                        {dayIndex === 0 && (
                                            <td rowSpan={4} className="border px-2 py-6 text-center rotate-90">
                                                <input
                                                    type="text"
                                                    value={specialPeriods[3]?.period  || ""}
                                                    onChange={(e) => handleSpecialChange(3, e.target.value)}
                                                    className="bg-transparent w-40 text-center focus:outline-none"
                                                />
                                            </td>
                                        )}

                                        {/* Periods 10, 11 */}
                                        {dayItem.periods.slice(5, 7).map((subj, idx) => (
                                            <td key={idx} className="border px-2 py-1 text-center">
                                                <input
                                                    type="text"
                                                    value={subj}
                                                    onChange={(e) => handlePeriodChange(dayIndex, 5 + idx, e.target.value)}
                                                    className="bg-transparent w-full text-center focus:outline-none"
                                                />
                                            </td>
                                        ))}

                                        {dayIndex === 0 && (
                                            <td rowSpan={5} className="border px-2 py-6 text-center rotate-90">
                                                <input
                                                    type="text"
                                                    value={specialPeriods[4]?.period  || ""}
                                                    onChange={(e) => handleSpecialChange(4, e.target.value)}
                                                    className="bg-transparent w-40 text-center focus:outline-none"
                                                />
                                            </td>
                                        )}

                                        {/* Periods 13, 14, 15 */}
                                        {dayItem.periods.slice(7).map((subj, idx) => (
                                            <td key={idx} className="border px-2 py-1 text-center">
                                                <input
                                                    type="text"
                                                    value={subj}
                                                    onChange={(e) => handlePeriodChange(dayIndex, 7 + idx, e.target.value)}
                                                    className="bg-transparent w-full text-center focus:outline-none"
                                                />
                                            </td>
                                        ))}

                                        {dayIndex === 0 && (
                                            <>
                                                <td rowSpan={4} className="border px-2 py-6 text-center rotate-90">
                                                    <input
                                                        type="text"
                                                        value={specialPeriods[5]?.period  || ""}
                                                        onChange={(e) => handleSpecialChange(5, e.target.value)}
                                                        className="bg-transparent w-40 text-center focus:outline-none"
                                                    />
                                                </td>
                                                <td rowSpan={4} className="border px-2 py-6 text-center rotate-90">
                                                    <input
                                                        type="text"
                                                        value={specialPeriods[6]?.period  || ""}
                                                        onChange={(e) => handleSpecialChange(6, e.target.value)}
                                                        className="bg-transparent w-40 text-center focus:outline-none"
                                                    />
                                                </td>
                                            </>
                                        )}
                                        <td className="border px-2 py-1 text-center">
                                            <button
                                                onClick={() => handleSavePeriod(dayItem.day)}
                                                className="bg-[#691e78] text-white text-xs px-3 py-1 rounded hover:bg-[#691e78]/90 transition"
                                                disabled={loading}
                                            >
                                                Save
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    {fridayTimeSlots.map((time, i) => (
                                        <td key={i}>
                                            <p className="text-xs">
                                                <input
                                                    type="text"
                                                    value={time.start}
                                                    onChange={(e) =>
                                                        handleTimeChange(fridayTimeSlots, setFridayTimeSlots, i, "start", e.target.value)
                                                    }
                                                    className="bg-transparent w-14 p-1 focus:outline-none"
                                                />
                                            </p>
                                            <p className="text-xs">
                                                <input
                                                    type="text"
                                                    value={time.end || ""}
                                                    onChange={(e) =>
                                                        handleTimeChange(fridayTimeSlots, setFridayTimeSlots, i, "end", e.target.value)
                                                    }
                                                    className="bg-transparent w-14 p-1 focus:outline-none"
                                                />
                                            </p>
                                        </td>
                                    ))}
                                    <th>
                                        <button
                                            type="submit"
                                            className="bg-[#691e78] py-1 px-3 rounded text-white text-sm hover:bg-[#691e78]/90 transition"
                                            disabled={loading}
                                            onClick={() => handleSave("f")}
                                        >
                                            Save
                                        </button>
                                    </th>
                                </tr>
                            </tbody>
                        </table>
                        <Box textAlign="right" mt={2}>
                            <button
                                onClick={handleSaveSpecialPeriod}
                                className="bg-[#691e78] text-white text-sm px-3 py-1 rounded hover:bg-[#691e78]/90 transition"
                                disabled={loading}
                            >
                                Save Special Periods
                            </button>
                        </Box>
                    </Box>
                </>
            }
        </Box>
    );
};
